import './App.css';
import * as React from 'react';
import Header from "./HeaderAndFooter/Header";
import {
	BrowserRouter, Routes, Route,
	useLocation, Navigate, useNavigate
} from "react-router-dom";
import MainMenu from "./Components/MainMenu";
import ManageDepartments from "./Components/ManageDepartments/ManageDepartments";
import Footer from "./HeaderAndFooter/Footer";
import ManageCategory from "./Components/ManageDepartments/ManageCategory";
import ManageSubCategory from "./Components/ManageDepartments/ManageSubCategory";
import ManageProducts from "./Components/AddProducts/ManageProducts";
import EditProduct from "./Components/EditProducts/EditProduct";
import AddProducts from "./Components/AddProducts/AddProducts";
import HomePage from "./Components/HomePage/HomePage";
import SignUp from "./Components/SignUp/SignUp";
import SignIn from "./Components/SignIn/SignIn";
import ProtectedRoute from "./ProtectedRoute";
import ManagePayments from "./Components/ManagePayments/ManagePayments";
import ManageOrders from "./Components/ManageOrders/ManageOrders";
import ManageTransactions from "./Components/ManageTransactions/ManageTransactions";
import {theme} from "./theme";
import {ThemeProvider} from "@mui/material/styles";
import {getAuthSession, getAuthStorage, normalizeSessionExpiry, performMigrationCleanup} from "./utils/authSession";
import {logoutCleanup} from "./store/logoutCleanup";

const AppContent = () => {

	const location = useLocation();
	const navigate = useNavigate();
	const hideHeaderRoutes = ["/", "/signup", "/login"];
	const shouldHideHeader = hideHeaderRoutes.includes(location.pathname);

	const {isLoggedIn} = getAuthSession();

	const ABSOLUTE_SESSION_MS = 60 * 60 * 1000; // 1 hour
	const absoluteLogoutTimeoutRef = React.useRef(null);
	const sanityIntervalRef = React.useRef(null);

	React.useEffect(() => {
		performMigrationCleanup();
	}, []);

	const logout = React.useCallback(() => {
		if (absoluteLogoutTimeoutRef.current) clearTimeout(absoluteLogoutTimeoutRef.current);
		if (sanityIntervalRef.current) clearInterval(sanityIntervalRef.current);

		logoutCleanup();
		navigate('/login', {replace: true});
	}, [navigate]);

	const getServerExpiresAt = () => {
		const stored = localStorage.getItem('sessionExpiresAt') ||
			sessionStorage.getItem('sessionExpiresAt') ||
			localStorage.getItem('expiresAt') ||
			sessionStorage.getItem('expiresAt'); // ISO string expected
		const normalizedExpiry = normalizeSessionExpiry(stored);
		if (!normalizedExpiry) return null;
		const t = Date.parse(normalizedExpiry);
		return Number.isNaN(t) ? null : t;
	};

	const ensureExpirySet = () => {
		// Prefer server-provided expiry. If not present, set fallback = now + 1h
		const serverExpiry = getServerExpiresAt();
		if (serverExpiry) return serverExpiry;

		const fallbackExpiry = Date.now() + ABSOLUTE_SESSION_MS;
		getAuthStorage().setItem('sessionExpiresAt', new Date(fallbackExpiry).toISOString());
		return fallbackExpiry;
	};

	const scheduleAbsoluteLogout = React.useCallback(() => {
		if (absoluteLogoutTimeoutRef.current) clearTimeout(absoluteLogoutTimeoutRef.current);
		if (sanityIntervalRef.current) clearInterval(sanityIntervalRef.current);

		const expiryMs = ensureExpirySet();
		const remaining = expiryMs - Date.now();

		if (remaining <= 0) {
			logout();
			return;
		}

		// Primary timeout at exact expiry
		absoluteLogoutTimeoutRef.current = setTimeout(logout, remaining);

		// Safety check for clock changes / multi-tab edits every 30s
		sanityIntervalRef.current = setInterval(() => {
			const exp = getServerExpiresAt() ?? expiryMs;
			if (Date.now() >= exp) logout();
		}, 30000);
	}, [logout]);

	React.useEffect(() => {
		if (!isLoggedIn) {
			if (absoluteLogoutTimeoutRef.current) clearTimeout(absoluteLogoutTimeoutRef.current);
			if (sanityIntervalRef.current) clearInterval(sanityIntervalRef.current);
			return;
		}

		// Initialize the absolute session timer
		scheduleAbsoluteLogout();

		// Keep tabs/windows in sync
		const onStorage = (e) => {
			if (e.key === 'isLoggedIn' && e.newValue === 'false') {
				logout();
			}
			if (e.key === 'sessionExpiresAt') {
				scheduleAbsoluteLogout(); // server refreshed token or another tab changed expiry
			}
		};
		window.addEventListener('storage', onStorage);

		return () => {
			if (absoluteLogoutTimeoutRef.current) clearTimeout(absoluteLogoutTimeoutRef.current);
			if (sanityIntervalRef.current) clearInterval(sanityIntervalRef.current);
			window.removeEventListener('storage', onStorage);
		};
	}, [isLoggedIn, scheduleAbsoluteLogout, logout]);

	if (isLoggedIn) {
		const exp = getServerExpiresAt();
		if (exp && Date.now() > exp) {
			// Let the effect run and perform logout immediately
			return null;
		}
	}

	if (location.pathname === '/' && isLoggedIn) {
		return <Navigate to="/home"/>;
	}

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				minHeight: '100vh',
				paddingRight: '0px'
			}}
		>
			<ThemeProvider theme={theme}>
				{!shouldHideHeader && <Header/>}
				<Routes>
					<Route path="/" element={<HomePage/>}/>
					<Route path="/signup" element={<SignUp/>}/>
					<Route path="/login" element={<SignIn/>}/>

					<Route path="/home" element={<ProtectedRoute element={<MainMenu/>}/>}/>
					<Route path="/md" element={<ProtectedRoute element={<ManageDepartments/>}/>}/>
					<Route path="/sc" element={<ProtectedRoute element={<ManageCategory/>}/>}/>
					<Route path="/ssc" element={<ProtectedRoute element={<ManageSubCategory/>}/>}/>
					<Route path="/mp" element={<ProtectedRoute element={<ManageProducts/>}/>}/>
					<Route path="/ep/:subcategory" element={<ProtectedRoute element={<EditProduct/>}/>}/>
					<Route path="/ap/:subcategory" element={<ProtectedRoute element={<AddProducts/>}/>}/>
					<Route path="/payments" element={<ProtectedRoute element={<ManagePayments/>}/>}/>
					<Route path="/orders" element={<ProtectedRoute element={<ManageOrders/>}/>}/>
					<Route path="/transactions" element={<ProtectedRoute element={<ManageTransactions/>}/>}/>
					<Route path="*" element={<Navigate to="/"/>}/>
				</Routes>
				{!shouldHideHeader && <Footer/>}
			</ThemeProvider>
		</div>
	);
};

const App = () => (
	<React.Fragment>
		<BrowserRouter>
			<AppContent/>
		</BrowserRouter>
	</React.Fragment>
);

export default App;
